import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {Box, Chip, Paper, Stack, Typography} from '@mui/material';
import React from 'react';

import {
  ContactBasicFragment,
  ContactBasicFragment_dataValidation_errors,
} from '../../schema-types/ContactBasicFragment';
import {themeColors} from '../../theme/Theme';

export type ContactValidationIssue = {
  contactName: string;
  errors: ContactBasicFragment_dataValidation_errors[];
};

export function getContactValidationIssues(
  contacts: Pick<ContactBasicFragment, 'id' | 'simplifiedName' | 'dataValidation'>[]
): ContactValidationIssue[] {
  return contacts
    .filter((contact) => contact.dataValidation && !contact.dataValidation.isValid)
    .map((contact) => ({
      contactName: contact.simplifiedName || 'Unknown Contact',
      // Backend returns errors unordered. Sort alphabetically by field for a
      // stable display order across contacts
      errors: [...contact.dataValidation!.errors].sort((a, b) =>
        a.field.localeCompare(b.field)
      ),
    }));
}

interface KeyClientDataBannerProps {
  issues: ContactValidationIssue[];
  isSFP: boolean;
}

export const KeyClientDataBanner: React.FC<KeyClientDataBannerProps> = ({
  issues,
  isSFP,
}) => {
  if (issues.length === 0) {
    return null;
  }

  const totalErrors = issues.reduce((sum, i) => sum + i.errors.length, 0);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        borderLeft: `4px solid ${themeColors.darkRed}`,
        backgroundColor: themeColors.lightAlert,
        borderRadius: 1.5,
        overflow: 'hidden',
      }}
    >
      <Box sx={{px: 2.5, py: 2}}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
          <ErrorOutlineIcon sx={{color: themeColors.darkRed, fontSize: 22}} />
          <Typography variant="subtitle1" fontWeight={600} color="text.primary">
            Key Client Data Issues — Full Submission Blocked
          </Typography>
          <Chip
            label={`${totalErrors} ${totalErrors === 1 ? 'issue' : 'issues'}`}
            size="small"
            sx={{
              ml: 'auto',
              fontWeight: 600,
              fontSize: '0.7rem',
              backgroundColor: themeColors.darkRed,
              color: themeColors.white,
              height: 22,
            }}
          />
        </Stack>

        {isSFP && (
          <Typography variant="body2" sx={{mb: 1.5, color: 'text.secondary'}}>
            The following data issues must be resolved in the eCQ before full submission
            can proceed.
            <br />
            Note: Planning Support will need to merge the changes into CURO before the
            updates are reflected here.
          </Typography>
        )}

        <Stack spacing={1.5}>
          {issues.map((issue) => (
            <Box
              key={issue.contactName}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.6)',
                borderRadius: 1,
                px: 2,
                py: 1.5,
              }}
            >
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{mb: 0.75, color: 'text.primary'}}
              >
                {issue.contactName}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {issue.errors.map((error) => (
                  <Chip
                    key={`${issue.contactName}-${error.field}-${error.message}`}
                    icon={
                      <ErrorOutlineIcon
                        sx={{fontSize: 14, color: `${themeColors.darkRed} !important`}}
                      />
                    }
                    label={error.message}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: themeColors.darkRed,
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      height: 26,
                      '& .MuiChip-label': {py: 0},
                    }}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </Box>
    </Paper>
  );
};
